import { useEffect, useMemo, useState } from 'react';
import { useShelvingStore } from '@/lib/shelving-store';
import { useLanguageStore } from '@/lib/language-store';
import { useAuthStore } from '@/lib/auth-store';
import { usePricingStore, resolvePricingContext } from '@/lib/pricing-store';
import { fetchMaterials, MaterialData } from '@/lib/materials-service';
import { calculateBOM, BOMSummary } from '@/lib/bom-calculator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface BOMDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BOMDialog({ open, onOpenChange }: BOMDialogProps) {
  const [bom, setBOM] = useState<BOMSummary | null>(null);
  const [materials, setMaterials] = useState<MaterialData[]>([]);
  const { getTranslation, currentLanguage } = useLanguageStore();
  const { user, profile, reseller } = useAuthStore();
  const { simulatedReseller } = usePricingStore();

  const showPrices = !!user;

  const discountPercent = simulatedReseller?.discount_percent ?? reseller?.discount_percent ?? null;
  const marginPercent = simulatedReseller?.margin_percent ?? reseller?.margin_percent ?? null;
  const role = profile?.role ?? null;

  const pricingContext = useMemo(
    () => resolvePricingContext(role, reseller, simulatedReseller),
    [role, discountPercent, marginPercent, simulatedReseller?.id]
  );

  const height = useShelvingStore((state) => state.height);
  const width = useShelvingStore((state) => state.width);
  const depth = useShelvingStore((state) => state.depth);
  const shelfCount = useShelvingStore((state) => state.shelfCount);
  const sectionCount = useShelvingStore((state) => state.sectionCount);
  const sides = useShelvingStore((state) => state.sides);
  const frameState = useShelvingStore((state) => state.frameState);
  const model = useShelvingStore((state) => state.model);

  useEffect(() => {
    fetchMaterials().then(setMaterials);
  }, []);

  useEffect(() => {
    if (materials.length === 0) return;
    const config = { height, width, depth, shelfCount, sectionCount, sides, frameState, model };
    setBOM(calculateBOM(materials, config, pricingContext ?? undefined));
  }, [materials, height, width, depth, shelfCount, sectionCount, sides, frameState, model, pricingContext]);

  const getItemDescription = (item: { description: string; descriptionDE: string }) => {
    if (currentLanguage === 'de') return item.descriptionDE;
    return item.description;
  };

  if (!bom) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{getTranslation('bom.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">{getTranslation('bom.loading')}</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>{getTranslation('bom.title')}</DialogTitle>
        </DialogHeader>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800">
                <tr>
                  <th className="text-left p-3 font-medium">{getTranslation('bom.material.id')}</th>
                  <th className="text-left p-3 font-medium">{getTranslation('bom.description')}</th>
                  <th className="text-center p-3 font-medium">{getTranslation('bom.unit')}</th>
                  <th className="text-right p-3 font-medium">{getTranslation('bom.qty')}</th>
                  {showPrices && <th className="text-right p-3 font-medium">{getTranslation('bom.unit.price')}</th>}
                  {showPrices && <th className="text-right p-3 font-medium">{getTranslation('bom.total')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {bom.items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="p-3 font-mono text-xs">{item.materialId}</td>
                    <td className="p-3">
                      <div className="flex flex-col">
                        <span className="font-medium">{getItemDescription(item)}</span>
                        <span className="text-xs text-gray-500">{item.category}</span>
                      </div>
                    </td>
                    <td className="text-center p-3">{item.unit}</td>
                    <td className="text-right p-3 font-semibold">{item.quantity}</td>
                    {showPrices && <td className="text-right p-3">€{item.unitPrice.toFixed(2)}</td>}
                    {showPrices && (
                      <td className="text-right p-3 font-semibold">
                        €{item.totalPrice.toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {showPrices && (
                <tfoot className="bg-gray-50 dark:bg-gray-800 font-bold border-t border-gray-200 dark:border-gray-700">
                  <tr>
                    <td colSpan={4} className="p-3 text-right">{getTranslation('bom.total')}:</td>
                    <td className="p-3 text-right text-green-600 dark:text-green-400" colSpan={2}>
                      €{bom.totalPrice.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
